import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UsersRound, Clock3, Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageView, PageButtons, PageRows, BlockedAside, errText, fmt } from "./kit";
import { Stepper, Notice } from "./v3";

const STEPS = [["bearbeiten", "Details"], ["zugang", "Zugang"], ["plaetze", "Plätze"], ["vorschau", "Vorschau"]];

// C47/C48: creation runs in four steps; each step saves through the existing endpoint of that part.
export function ActivityStepper({ id, current }) {
  return (
    <nav aria-label="Schritte zum Anlegen" data-testid="activity-steps">
      <ol className="grid grid-cols-4 gap-1 rounded-xl bg-[#ECE8F3] p-1">
        {STEPS.map(([slug, text]) => {
          const on = slug === current;
          const cls = `flex h-9 items-center justify-center rounded-lg px-1 text-center text-xs sm:text-sm ${on ? "bg-white font-bold text-brand-magenta shadow-sm" : "text-zinc-600"}`;
          return (
            <li key={slug}>
              {id && !on ? <Link to={`/aktivitaeten/${id}/${slug}`} className={`${cls} hover:text-brand-purple`} data-testid={`activity-step-${slug}`}>{text}</Link>
                : <span aria-current={on ? "step" : undefined} aria-disabled={!on || undefined} className={`${cls} ${on ? "" : "opacity-60"}`} data-testid={`activity-step-${slug}`}>{text}</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

const ACCESS_LABEL = { ja: "Vorhanden", nein: "Nicht vorhanden", unbekannt: "Unbekannt" };
const ACCESS_TONE = {
  ja: "border-emerald-200 bg-emerald-50 text-emerald-800", nein: "border-zinc-200 bg-zinc-100 text-zinc-700",
  unbekannt: "border-amber-200 bg-[#FFF6DD] text-[#8A5A00]",
};
export const ACCESS_RADIO = Object.entries(ACCESS_LABEL).map(([value, label]) => ({ value, label }));

export function AccessBadge({ value, testId }) {
  const v = ACCESS_LABEL[value] ? value : "unbekannt";
  return <span data-testid={testId} className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${ACCESS_TONE[v]}`}>{ACCESS_LABEL[v]}</span>;
}

export function SeatsStep({ d, reload }) {
  const a = d.activity;
  const s = d.summary;
  const navigate = useNavigate();
  const [cap, setCap] = useState(s.capacity || Math.max(1, s.seats_taken));
  const [comp, setComp] = useState(s.companion_capacity || 0);
  const [reason, setReason] = useState("Plätze beim Anlegen festgelegt");
  const [busy, setBusy] = useState(false);
  const draft = a.status === "entwurf";
  const editable = d.can.manage && !["abgesagt", "archiviert"].includes(a.status);
  const save = async (to) => {
    if (cap === s.capacity && comp === s.companion_capacity) { navigate(to, { replace: true }); return; }
    if (!reason.trim()) { toast.error("Bitte einen Grund angeben."); return; }
    setBusy(true);
    try {
      await api.put(`/manage/activities/${a.id}/seats`, { capacity: cap, companion_capacity: comp, reason });
      toast.success("Plätze gespeichert.");
      reload();
      navigate(to, { replace: true });
    } catch (err) {
      toast.error(errText(err));
      setBusy(false);
    }
  };
  return (
    <PageView page="plaetze" hideLink testId="activity-seats-step" title={draft ? "Aktivität anlegen: Plätze" : "Plätze und Begleitplätze"}
      subtitle="Teilnehmenden- und Begleitplätze werden getrennt vergeben und bestätigt." subject={a.title_de}
      top={draft && <ActivityStepper id={a.id} current="plaetze" />}>
      {({ back }) => (
        <>
          {editable ? (
            <>
              <Stepper id="step-capacity" label="Teilnehmendenplätze" icon={UsersRound} value={cap} min={Math.max(1, s.seats_taken)} max={500} onChange={setCap} />
              <Stepper id="step-companions" label="Begleitplätze" icon={UsersRound} value={comp} min={s.companions_confirmed} max={100} onChange={setComp} />
            </>
          ) : <PageRows rows={[["Teilnehmendenplätze", s.capacity ?? "nicht festgelegt"], ["Begleitplätze", String(s.companion_capacity || 0)]]} testId="activity-seats-step" />}
          <PageRows testId="activity-seats-step" rows={[
            ["Bereits bestätigt", `${s.seats_taken} Teilnahme(n) · ${s.companions_confirmed} Begleitplatz/-plätze`],
            ["Warteliste", s.waitlist ? `${s.waitlist} Person(en) · Nachrücken nur nach Entscheidung` : "Niemand auf der Warteliste"]]} />
          <Notice title="Getrennte Platztypen" text="Eine Teilnahmeanfrage enthält keine automatische Begleitplatzbestätigung." testId="step-seats-rule" />
          {editable && (
            <label htmlFor="step-seats-reason" className="flex flex-col gap-1">
              <span className="text-sm font-bold text-brand-purple">Grund der Platzregel *</span>
              <textarea id="step-seats-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="step-seats-reason"
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta" />
            </label>
          )}
          <PageButtons primary={editable ? (draft ? "Weiter zur Vorschau" : "Plätze speichern") : null} busy={busy}
            onPrimary={() => save(draft ? `/aktivitaeten/${a.id}/vorschau` : back)} back={draft ? `/aktivitaeten/${a.id}/zugang` : back} testId="activity-seats-step" />
        </>
      )}
    </PageView>
  );
}

// C49: what members will see, before the activity is sent for review or published.
export function PreviewStep({ d, features, reload }) {
  const a = d.activity;
  const s = d.summary;
  const cur = a.access?.features || {};
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const draft = a.status === "entwurf";
  const unknown = Object.keys(features).filter((k) => (cur[k]?.value || "unbekannt") === "unbekannt").length;
  const sources = [...new Set(Object.values(cur).map((f) => f?.source).filter(Boolean))];
  const stand = Object.values(cur).map((f) => f?.updated_at).filter(Boolean).sort().pop();
  const when = `${fmt(a.date, a.timezone)}${a.end ? ` bis ${a.end} Uhr` : ""}`;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/manage/activities/${a.id}/submit`);
      toast.success("Zur Prüfung gesendet.");
      reload();
      navigate(`/aktivitaeten/${a.id}`, { replace: true });
    } catch (err) {
      toast.error(errText(err));
      setBusy(false);
    }
  };
  const primary = draft && d.can.manage ? "Zur Prüfung senden" : a.status === "zur_pruefung" && d.can.approve ? "Veröffentlichung prüfen" : null;
  return (
    <PageView page="vorschau" label="Vorschau" icon={Eye} testId="preview-activity" hideLink={!["entwurf", "zur_pruefung"].includes(a.status)}
      title="Aktivitätsvorschau vor Freigabe" subtitle="Prüfe die Vorschau mit den Angaben, die Mitglieder tatsächlich sehen sollen." subject={a.title_de}
      top={draft && <ActivityStepper id={a.id} current="vorschau" />}
      aside={<>
        <BlockedAside icon={UsersRound} title="Plätze und Begleitung" lavender
          text={`${s.capacity ?? "–"} Teilnehmendenplätze und ${s.companion_capacity || 0} Begleitplätze werden getrennt bestätigt.`} />
        <BlockedAside icon={Clock3} title="Deine Anfrage" text="Bis zu einer ausdrücklichen Annahme ist die Teilnahme noch nicht bestätigt." />
      </>}>
      {({ back }) => (
        <>
          <article className="rounded-2xl border border-[#E6E1F0] bg-white p-5" data-testid="preview-card">
            <h2 className="text-2xl font-bold text-brand-purple">{a.title_de}</h2>
            <p className="mt-2 text-sm text-zinc-700">{when}</p>
            <p className="text-sm text-zinc-600">{a.location}</p>
            <span className="mt-3 inline-flex rounded-full border border-amber-200 bg-[#FFF6DD] px-2.5 py-0.5 text-xs font-semibold text-[#8A5A00]" data-testid="preview-state">
              {a.status === "veröffentlicht" ? "Veröffentlicht" : "Vorschau · Noch nicht veröffentlicht"}
            </span>
            {a.description_de && <p className="mt-3 text-sm text-zinc-600">{a.description_de}</p>}
          </article>
          <PageRows testId="preview-activity" rows={[
            ["Aktivität", a.title_de], ["Termin", when],
            ["Teilnehmendenplätze", s.capacity ? `${s.confirmed} von ${s.capacity} bestätigt · ${s.free} frei` : "nicht festgelegt"],
            ["Prüfstand", unknown ? `Zugangsangaben teils unbekannt (${unknown} offen)` : "Zugangsangaben vollständig"]]} />
          <h2 className="pt-2 text-lg font-bold text-brand-purple">Zugangsangaben</h2>
          <ul className="space-y-2">
            {Object.entries(features).map(([k, text]) => (
              <li key={k} className="flex items-center justify-between gap-2 rounded-xl border border-[#E6E1F0] bg-white px-4 py-2.5" data-testid={`preview-access-${k}`}>
                <span className="text-sm font-semibold text-brand-purple">{text}</span><AccessBadge value={cur[k]?.value} />
              </li>
            ))}
          </ul>
          <p className="text-xs text-zinc-500" data-testid="preview-access-source">Quelle: {sources.join(", ") || "noch keine Angabe"}{stand ? ` · Stand ${fmt(stand)}` : ""}</p>
          <PageButtons primary={primary} busy={busy} onPrimary={draft ? submit : undefined}
            primaryTo={a.status === "zur_pruefung" ? `/aktivitaeten/${a.id}/veroeffentlichen` : undefined}
            back={draft ? `/aktivitaeten/${a.id}/plaetze` : back} testId="preview-activity" />
        </>
      )}
    </PageView>
  );
}
